import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { 
  Trophy, 
  Users, 
  ArrowLeft,
  Loader2,
  Gem
} from 'lucide-react'
import { userApi } from '../services'
import useAppStore from '../store/useAppStore'
import toast from 'react-hot-toast'
import ProfileLink from '../components/ProfileLink'
import TeamTag from '../components/TeamTag'

// Утилита для форматирования чисел с пробелами
const formatNumber = (num) => {
  return num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') || '0'
}

const ReferralTop = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const isGuest = useAppStore((state) => state.isGuest)
  const openLogin = useAppStore((state) => state.openLogin)
  
  // Состояния
  const [topData, setTopData] = useState(null) // Данные топа с API
  const [isLoading, setIsLoading] = useState(true) // Состояние загрузки
  const [selectedPeriod, setSelectedPeriod] = useState('week') // Выбранный период (today/week/month)
  
  // Загрузка данных топа при монтировании
  useEffect(() => {
    loadTopData()
  }, [])
  
  // Функция загрузки данных с API
  const loadTopData = async () => {
    setIsLoading(true)
    try {
      const response = await userApi.getReferralTop()
      
      if (response.error) {
        toast.error(response.error)
        return
      }
      
      if (response.referral_top) {
        setTopData(response.referral_top)
      } else {
        toast.error(t('referrals.topLoadError'))
      }
    } catch (error) {
      console.error('Ошибка загрузки топа партнеров:', error)
      if (!error.isNotified && !error.isHandledAuthError) {
        toast.error(error.isBackendError ? error.message : t('referrals.topLoadError'))
      }
    } finally {
      setIsLoading(false)
    }
  }
  
  // Получение массива лидеров для выбранного периода
  const getLeadersForPeriod = () => {
    if (!topData || !topData[selectedPeriod]) return []
    
    const periodData = topData[selectedPeriod]
    const leaders = []
    
    // Преобразуем объект с числовыми ключами в массив
    for (let i = 1; i <= 10; i++) {
      if (periodData[i.toString()]) {
        leaders.push({
          position: i,
          ...periodData[i.toString()]
        })
      }
    }
    
    return leaders
  }
  
  // Получение позиции текущего пользователя для выбранного периода
  const getUserPosition = () => {
    if (!topData) return 0
    
    const positionKey = `position_in_top_${selectedPeriod}`
    // return 4 // Мок данные для теста
    return topData[positionKey] || 0
  }
  
  const leaders = getLeadersForPeriod()
  const userPosition = getUserPosition()
  
  // Получение стилей для позиций (топ-3 с цветами, is_you с зеленым)
  const getMedalStyles = (position, isYou) => {
    // Если это текущий пользователь - зеленая подсветка
    if (isYou) {
      return {
        bgColor: 'bg-accent-primary/20',
        borderColor: 'border-accent-primary/50',
        textColor: 'text-accent-primary'
      }
    }
    
    // Иначе стандартные цвета для топ-3
    switch(position) {
      case 1:
        return {
          bgColor: 'bg-yellow-500/20',
          borderColor: 'border-yellow-500/50',
          textColor: 'text-yellow-500'
        }
      case 2:
        return {
          bgColor: 'bg-gray-400/20',
          borderColor: 'border-gray-400/50',
          textColor: 'text-gray-400'
        }
      case 3:
        return {
          bgColor: 'bg-orange-600/20',
          borderColor: 'border-orange-600/50',
          textColor: 'text-orange-600'
        }
      default:
        return {
          bgColor: 'bg-surface',
          borderColor: 'border-white/10',
          textColor: 'text-text-primary'
        }
    }
  }

  return (
    <div className="pb-10 lg:max-w-2xl lg:mx-auto">
      {/* Хедер с кнопкой назад */}
      <div className="top-0 z-10  border-b border-white/10 px-6 py-3">
        <div className="flex items-center gap-4 justify-center">
          <button 
            onClick={() => navigate('/referrals')}
            className="p-2 rounded-lg bg-surface hover:bg-opacity-80 transition-all"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-accent-primary">
              {t('referrals.topPartners')}
            </h1>
          </div>
        </div>
      </div>

      {/* Контент */}
      <div className="p-4 space-y-4">
        {/* Табы периодов */}
        <div className="grid grid-cols-3 gap-2 bg-surface rounded-lg p-1">
          <button
            onClick={() => setSelectedPeriod('today')}
            className={`py-2.5 px-3 rounded-lg text-sm font-medium transition-all ${
              selectedPeriod === 'today'
                ? 'bg-accent-primary text-dark-primary'
                : 'text-text-secondary hover:text-white'
            }`}
          >
            {t('referrals.periodDay')}
          </button>
          <button
            onClick={() => setSelectedPeriod('week')}
            className={`py-2.5 px-3 rounded-lg text-sm font-medium transition-all ${
              selectedPeriod === 'week'
                ? 'bg-accent-primary text-dark-primary'
                : 'text-text-secondary hover:text-white'
            }`}
          >
            {t('referrals.periodWeek')}
          </button>
          <button
            onClick={() => setSelectedPeriod('month')}
            className={`py-2.5 px-3 rounded-lg text-sm font-medium transition-all ${
              selectedPeriod === 'month'
                ? 'bg-accent-primary text-dark-primary'
                : 'text-text-secondary hover:text-white'
            }`}
          >
            {t('referrals.periodMonth')}
          </button>
        </div>

        {/* Состояние загрузки */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <Loader2 className="animate-spin text-accent-primary" size={40} />
          </div>
        )}

        {/* Контент с данными */}
        {!isLoading && topData && (
          <>
            {/* Блок позиции текущего пользователя */}
            {isGuest ? (
              <button type="button" onClick={openLogin} className="w-full rounded-xl border border-accent-primary/30 bg-accent-primary/10 p-3 font-semibold text-accent-primary">
                {t('guest.login')}
              </button>
            ) : userPosition > 0 && (
              <div className=" p-3">
                <div className="flex items-center justify-center gap-2">
                  <Trophy className="text-accent-primary" size={18} />
                  <span className="text-sm text-text-secondary">{t('referrals.yourPosition')}</span>
                  <span className="text-xl font-bold text-accent-primary">#{userPosition}</span>
                </div>
              </div>
            )}

            {/* Список лидеров */}
            <div className="space-y-3">
              {leaders.length === 0 ? (
                <div className="text-center py-12">
                  <Trophy className="mx-auto text-text-secondary mb-4" size={48} />
                  <p className="text-text-secondary">{t('referrals.noData')}</p>
                </div>
              ) : (
                leaders.map((leader) => {
                  const styles = getMedalStyles(leader.position, leader.is_you)
                  
                  return (
                    <motion.div
                      key={leader.position}
                      className={`${styles.bgColor} border ${styles.borderColor} rounded-lg p-4 transition-all hover:scale-[1.02] relative overflow-hidden`}
                      whileHover={{ scale: 1.02 }}
                    >
                      {/* Анимированный блик для карточки текущего пользователя */}
                      {leader.is_you && (
                        <motion.div
                          className="absolute inset-0 bg-white/5"
                          animate={{ x: ['-100%', '100%'] }}
                          transition={{
                            duration: 3,
                            repeat: Infinity,
                            repeatDelay: 1,
                            ease: "linear"
                          }}
                        />
                      )}
                      
                      <div className="flex items-center gap-3 relative z-10">
                        {/* Позиция */}
                        <div className="flex items-center justify-center w-12 h-12 bg-dark-primary/50 rounded-full relative">
                          <span className={`text-lg font-bold ${styles.textColor}`}>
                            #{leader.position}
                          </span>
                        </div>

                        {/* Аватар — кликабелен при user_info.slug !== null */}
                        <ProfileLink slug={leader.user_info?.slug} className="w-12 h-12 rounded-full overflow-hidden bg-dark-primary border-2 border-border flex-shrink-0 block">
                          <img
                            src={leader.user_info.ava}
                            alt={leader.user_info.name}
                            className="w-full h-full object-cover"
                          />
                        </ProfileLink>

                        {/* Информация о лидере */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-white truncate">
                            <TeamTag team={leader.user_info?.team} className="" />
                            <ProfileLink slug={leader.user_info?.slug}>
                              {leader.user_info.name.replace(/@cs_shot_bot/gi, '').trim()}
                            </ProfileLink>
                          </p>
                          <div className="flex items-center gap-4 mt-1">
                            <div className="flex items-center gap-1">
                              <Gem size={14} className="text-accent-primary" />
                              <span className="text-xs text-accent-primary">
                                {formatNumber(leader.earn)}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Users size={14} className="text-text-secondary" />
                              <span className="text-xs text-text-secondary">
                                {formatNumber(leader.all_ref_count)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )
                })
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default ReferralTop
